import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomDynamicFormState } from "@midland-industries/Store/Data/DynamicForms/DynamicFormReducer";
import setDynamicFormFieldValue from "@midland-industries/Store/Data/DynamicForms/Handlers/SetDynamicFormFieldValue";
import { DynamicFormCheckboxGroupLabelPositions } from "@midland-industries/Types/DynamicFormModels";
import { determineFormFieldIndexes } from "@midland-industries/Utilities/DetermineFormFieldIndexes";
import { determineFormIndex } from "@midland-industries/Utilities/DetermineFormIndex";
import { DynamicFormContext } from "@midland-industries/Widgets/DynamicForms/DynamicForm";
import DynamicFormCheckbox, {
    DynamicFormCheckboxStyles,
} from "@midland-industries/Widgets/DynamicForms/DynamicFormCheckbox";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    key = "key",
    hasLabel = "hasLabel",
    label = "label",
    columns = "columns",
    requireOneSelection = "requireOneSelection",
    sendOnlyIfChecked = "sendOnlyIfChecked",
    topics = "topics",
}

interface TopicModel {
    fields: {
        key: string;
        isCheckedByDefault: boolean;
        labelPosition: DynamicFormCheckboxGroupLabelPositions;
        label: string;
        changeTrueFalseToYesNo: boolean;
    };
}

interface OwnProps {
    fields: {
        [fields.key]: string;
        [fields.hasLabel]: boolean;
        [fields.label]: string;
        [fields.columns]: number;
        [fields.requireOneSelection]: boolean;
        [fields.sendOnlyIfChecked]: boolean;
        [fields.topics]: TopicModel[];
    };
}

const mapStateToProps = (state: CustomDynamicFormState) => {
    const dynamicFormData = state.xcentiumDynamicForms;
    return {
        dynamicFormData,
    };
};

const mapDispatchToProps = {
    setDynamicFormFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface DynamicFormCheckboxGroupStyles {
    wrapper?: InjectableCss;
    checkboxGroupContainer?: GridContainerProps;
    checkboxGroupContainerError?: GridContainerProps;
    labelWrapper?: InjectableCss;
    label?: TypographyPresentationProps;
    checkboxGridItem?: GridItemProps;
    checkbox?: DynamicFormCheckboxStyles;
    errorWrapper?: InjectableCss;
    error?: TypographyPresentationProps;
}

export const dynamicFormCheckboxGroupStyles: DynamicFormCheckboxGroupStyles = {
    wrapper: {
        css: css`
            width: 100%;
            margin-top: 0.5em;
            margin-bottom: 1em;
        `,
    },
    checkboxGroupContainer: {
        gap: 0,
        offsetCss: css`
            padding: 10px;
            border: 0 solid black;
        `,
    },
    checkboxGroupContainerError: {
        gap: 0,
        offsetCss: css`
            padding: 10px;
            border: 2px solid #e64e25;
        `,
    },
    labelWrapper: {
        css: css`
            width: 100%;
            margin-bottom: 10px;
        `,
    },
    label: {
        weight: 600,
    },
    checkboxGridItem: {
        width: 12,
    },
    errorWrapper: {
        css: css`
            width: 100%;
            margin-top: 10px;
        `,
    },
    error: {
        css: css`
            color: #e64e25;
            font-size: 15px;
            font-weight: 600;
        `,
    },
};

export const styles = dynamicFormCheckboxGroupStyles;

export type DynamicFormCheckboxGroupValidator = {
    fieldKey: string;
    validate: () => {
        isValid: boolean;
        isChecked: boolean;
        errorMessage: string;
    };
};

export type DynamicFormCheckboxGroupContextType = {
    groupKey: string;
    validators: DynamicFormCheckboxGroupValidator[];
};

export const DynamicFormCheckboxGroupContext = React.createContext<DynamicFormCheckboxGroupContextType>({
    groupKey: "",
    validators: [] as DynamicFormCheckboxGroupValidator[],
});

const DynamicFormCheckboxGroup: React.FC<Props> = ({ fields, dynamicFormData, setDynamicFormFieldValue }) => {
    const { key, hasLabel, label, columns, requireOneSelection, sendOnlyIfChecked, topics } = fields;

    const [checkboxGroupErrorMessage, setCheckboxGroupErrorMessage] = useState("");
    const [fieldIndex, setFieldIndex] = useState(-1);
    const [formIndex, setFormIndex] = useState(-1);

    const [validators] = useState<DynamicFormCheckboxGroupValidator[]>([]);

    const dynamicFormContext = useContext(DynamicFormContext);

    useEffect(() => {
        initializeWidget();
    }, [dynamicFormData?.forms]);

    useEffect(() => {
        const groupHasError = !!(checkboxGroupErrorMessage && checkboxGroupErrorMessage.length > 0);
        setDynamicFormFieldValue({
            formName: dynamicFormContext.formName,
            formField: {
                key,
                group: "",
                label,
                isRequired: false,
                value: "",
                type: "DynamicFormCheckboxGroup",
                sendOnlyIfChecked: sendOnlyIfChecked || false,
                hasError: groupHasError,
                error: checkboxGroupErrorMessage,
            },
        });
    }, [checkboxGroupErrorMessage]);

    const getChildCountWithIsCheckedByDefault = () => {
        let childCount = 0;
        topics.forEach(topic => {
            if (topic.fields.isCheckedByDefault) {
                childCount += 1;
            }
        });
        return childCount;
    };

    const initializeWidget = () => {
        const formFieldIndexes = determineFormFieldIndexes(dynamicFormContext.formName, key, dynamicFormData);
        setFormIndex(formFieldIndexes.formIndex);
        setFieldIndex(formFieldIndexes.fieldIndex);

        // No form registered in state
        if (formFieldIndexes.formIndex < 0) {
            return;
        }

        // Widget already registered in state
        if (formFieldIndexes.formIndex > -1 && formFieldIndexes.fieldIndex > -1) {
            return;
        }

        let widgetHasError = false;
        let widgetError = "";

        if (requireOneSelection) {
            const childCount = getChildCountWithIsCheckedByDefault();
            if (childCount === 0) {
                widgetHasError = true;
                widgetError = siteMessage("DynamicForms_Require_One_Item_Checked") as string;
            }
        }

        setDynamicFormFieldValue({
            formName: dynamicFormContext.formName,
            formField: {
                key,
                group: "",
                label,
                isRequired: false,
                value: "",
                type: "DynamicFormCheckboxGroup",
                sendOnlyIfChecked: sendOnlyIfChecked || false,
                hasError: widgetHasError,
                error: widgetError,
            },
        });

        // Register the checkboxGroup validator into the dynamicFormContext
        RegisterDynamicFormCheckboxGroupValidator();
    };

    // Function is sent down to the DynamicFormCheckbox widgets that are added to the group.
    // When triggered by a child of the DynamicFormCheckboxGroup, the group will be validated.
    const validateCheckboxClick = () => {
        validateCheckboxGroup();
    };

    const RegisterDynamicFormCheckboxGroupValidator = () => {
        const widgetValidatorIndex = dynamicFormContext.validators.findIndex(v => v.fieldKey === key);
        if (widgetValidatorIndex > -1) {
            return;
        }
        const dynamicFormCheckboxGroupValidator = {
            fieldKey: key,
            validate: validateCheckboxGroup,
        };
        dynamicFormContext.validators.push(dynamicFormCheckboxGroupValidator);
    };

    const validateCheckboxGroup = () => {
        let errorMessage = "";
        let hasError = false;

        // Validate the checkboxes that are children of the checkboxGroup
        const widgetValidators: DynamicFormCheckboxGroupValidator[] = [];
        validators.forEach(validator => {
            if (validator) {
                widgetValidators.push(validator);
            }
        });

        const results = widgetValidators.map(v => v.validate());

        if (!results.every(o => o)) {
            errorMessage = "Checkbox group is not valid";
            hasError = true;
        }

        if (requireOneSelection) {
            let checked = false;
            results.forEach(item => {
                if (item.isChecked === true) {
                    checked = true;
                }
            });
            if (checked === false) {
                errorMessage = siteMessage("DynamicForms_Require_One_Item_Checked") as string;
                hasError = true;
            }
        }

        setDynamicFormFieldValue({
            formName: dynamicFormContext.formName,
            formField: {
                key,
                group: "",
                label,
                isRequired: false,
                value: "",
                type: "DynamicFormCheckboxGroup",
                sendOnlyIfChecked: sendOnlyIfChecked || false,
                hasError,
                error: errorMessage,
            },
        });

        setCheckboxGroupErrorMessage(errorMessage);

        return {
            isValid: !hasError,
            errorMessage,
        };
    };

    const getDisplayValidationErrors = () => {
        const formIndex = determineFormIndex(dynamicFormContext.formName, dynamicFormData).formIndex;
        if (formIndex < 0) {
            return false;
        }

        return dynamicFormData?.forms[formIndex]?.displayValidationErrors;
    };

    const getGridItemWidth = () => {
        switch (columns) {
            case 1:
                return [12, 12, 12, 12, 12] as GridWidths[];
            case 2:
                return [6, 6, 6, 6, 6] as GridWidths[];
            case 3:
                return [4, 4, 4, 4, 4] as GridWidths[];
            default:
                return [12, 12, 12, 12, 12] as GridWidths[];
        }
    };

    const getCheckboxGroupWidgets = () => {
        const formIndex = determineFormIndex(dynamicFormContext.formName, dynamicFormData).formIndex;
        if (formIndex < 0) {
            return [];
        }

        return dynamicFormData?.forms[formIndex]?.fields.filter(f => f.key.startsWith(`${key}:`));
    };

    /**
     * This function is assigned to the 'All' Checkbox if it exists as part of a group. The functionality is to apply
     * the same value (checked or unchecked) to the remainder of the checkbox group items.
     */
    const handleCheckboxChange: (event: React.SyntheticEvent<Element, Event>, value: boolean) => void = (
        event,
        value,
    ) => {
        const fields = getCheckboxGroupWidgets();
        for (let x = 0; x < fields.length; x += 1) {
            setDynamicFormFieldValue({
                formName: dynamicFormContext.formName,
                formField: {
                    key: fields[x].key,
                    group: fields[x].group,
                    label: fields[x].label,
                    isRequired: fields[x].isRequired,
                    value,
                    type: "DynamicFormCheckbox",
                    sendOnlyIfChecked: fields[x].sendOnlyIfChecked,
                    hasError: fields[x].hasError,
                    error: fields[x].error,
                },
            });
        }
    };

    const columnElements: JSX.Element[] = [];
    const gridItemWidth = getGridItemWidth();
    for (let x = 0; x < topics.length; x += 1) {
        const topicKey = `${key}:${topics[x].fields.key}`;
        const standardCheckbox = (
            <GridItem key={x} width={gridItemWidth}>
                <DynamicFormCheckbox
                    validateCheckboxClicked={validateCheckboxClick}
                    fields={{
                        key: topicKey,
                        group: key,
                        isRequired: false,
                        labelPosition: topics[x].fields.labelPosition,
                        label: topics[x].fields.label,
                        isCheckedByDefault: topics[x].fields.isCheckedByDefault,
                        changeTrueFalseToYesNo: topics[x].fields.changeTrueFalseToYesNo,
                        hintText: "",
                    }}
                ></DynamicFormCheckbox>
            </GridItem>
        );
        const allCheckbox = (
            <GridItem key={x} width={gridItemWidth}>
                <DynamicFormCheckbox
                    validateCheckboxClicked={validateCheckboxClick}
                    handleOnChange={handleCheckboxChange}
                    fields={{
                        key: topicKey,
                        group: key,
                        isRequired: false,
                        labelPosition: topics[x].fields.labelPosition,
                        label: topics[x].fields.label,
                        isCheckedByDefault: topics[x].fields.isCheckedByDefault,
                        changeTrueFalseToYesNo: topics[x].fields.changeTrueFalseToYesNo,
                        hintText: "",
                    }}
                ></DynamicFormCheckbox>
            </GridItem>
        );
        if (topics[x].fields.key.toLowerCase() === "all") {
            columnElements.push(allCheckbox);
        } else {
            columnElements.push(standardCheckbox);
        }
    }

    const getContainerStyles = () => {
        const displayValidationErrors = getDisplayValidationErrors();
        return displayValidationErrors && checkboxGroupErrorMessage
            ? styles.checkboxGroupContainerError
            : styles.checkboxGroupContainer;
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            {hasLabel && label.length > 0 && (
                <StyledWrapper {...styles.labelWrapper}>
                    <Typography {...styles.label}>{requireOneSelection ? `${label} *` : label}</Typography>
                </StyledWrapper>
            )}
            <DynamicFormCheckboxGroupContext.Provider value={{ groupKey: key, validators }}>
                <GridContainer {...getContainerStyles()}>{columnElements}</GridContainer>
            </DynamicFormCheckboxGroupContext.Provider>
            {getDisplayValidationErrors() && checkboxGroupErrorMessage && (
                <StyledWrapper {...styles.errorWrapper}>
                    <Typography {...styles.error}>{checkboxGroupErrorMessage}</Typography>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(DynamicFormCheckboxGroup);
